export type UserTier = 'basic' | 'dental' | 'super_admin';

const TIER_HIERARCHY_VALUES: Record<UserTier, number> = {
  'basic': 0,
  'dental': 1,
  'super_admin': 2,
};

/**
 * Checks if the user's current tier is sufficient to access a feature
 * requiring a minimum tier.
 *
 * @param currentUserTier The current tier of the user.
 * @param requiredMinimumTier The minimum tier required to access the feature.
 * @returns True if the user's tier is sufficient, false otherwise.
 *          Super_admin always has access unless the required tier is somehow higher (which shouldn't happen).
 */
export function isUserTierSufficient(
  currentUserTier: UserTier | undefined | null,
  requiredMinimumTier: UserTier
): boolean {
  if (!currentUserTier) {
    // If current tier is undefined or null, deny access
    return false;
  }

  // A user's tier value must be greater than or equal to the required tier's value.
  // Super_admin effectively has the highest possible tier value.
  return TIER_HIERARCHY_VALUES[currentUserTier] >= TIER_HIERARCHY_VALUES[requiredMinimumTier];
}

/**
 * Gets the next highest tier from the current tier.
 * e.g. basic -> dental
 * Returns null if already at the highest non-admin tier or if current tier is super_admin.
 * @param currentTier The user's current tier.
 * @returns The next UserTier or null.
 */
export function getNextTier(currentTier: UserTier | undefined | null): UserTier | null {
  if (!currentTier || currentTier === 'super_admin' || currentTier === 'dental') {
    return null;
  }
  if (currentTier === 'basic') return 'dental';
  return null;
}

const PLAN_TO_TIER_MAP: Record<string, UserTier> = {
  basic: 'basic',
  dental: 'dental',
};

const TIER_TO_PLAN_MAP: Record<UserTier, string | null> = {
  basic: 'basic',
  dental: 'dental',
  super_admin: null,
};

export function planCodeToTier(planCode: string | null | undefined): UserTier | null {
  if (!planCode) return null;
  // Legacy plan migration support
  const normalizedPlanCode = planCode === 'hygienist' ? 'basic' : planCode === 'free' ? 'basic' : planCode;
  return PLAN_TO_TIER_MAP[normalizedPlanCode] ?? null;
}

export function tierToPlanCode(tier: UserTier | null | undefined): string | null {
  if (!tier) return null;
  return TIER_TO_PLAN_MAP[tier] ?? null;
}
